import { useMutation } from '@apollo/client';
import { PublicApiKeyRotatedResponse, RotatePublicApiKeyInput } from '@provision/entity-types';
import { useCallback } from 'react';
import {
  GET_PUBLIC_API_KEYS_QUERY,
  ROTATE_PUBLIC_API_KEY_MUTATION,
} from 'src/graphql/entities/public-api-key/public-api-key.operations';
import { updateQueryWithInvalidation } from 'src/graphql/entities/public-api-key/public-api-key.utils';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';

interface RotatePublicApiKeyMutation {
  rotatePublicApiKey: PublicApiKeyRotatedResponse;
}

interface RotatePublicApiKeyMutationVariables {
  params: RotatePublicApiKeyInput;
}

export const useRotatePublicApiKey = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showErrorAlert } = useNotifications();

  const [rotateApiKeyMutation, { loading }] = useMutation<
    RotatePublicApiKeyMutation,
    RotatePublicApiKeyMutationVariables
  >(ROTATE_PUBLIC_API_KEY_MUTATION, {
    refetchQueries: [GET_PUBLIC_API_KEYS_QUERY],
    awaitRefetchQueries: true,
    update: updateQueryWithInvalidation(selectedPartnerId!),
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to regenerate API key, please try again');
    },
  });

  const rotatePublicApiKey = useCallback(
    async (apiKeyId: string) => {
      if (!selectedPartnerId) {
        throw new Error('Cannot rotate API key without a selected partner.');
      }
      const result = await rotateApiKeyMutation({
        variables: {
          params: { partnerId: selectedPartnerId, apiKeyId },
        },
      });
      return result.data?.rotatePublicApiKey;
    },
    [rotateApiKeyMutation, selectedPartnerId],
  );

  return { rotatePublicApiKey, loading };
};
